import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button,
  Typography, Grid, IconButton, Collapse,
  MenuItem,
  Menu
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { LeadFormActions } from '../../../Services/_actions/LeadForm/leadForm.actions.js';
import CreatEditMappingModal from './CreateEditMappingModal.tsx';
import CampaignHierarchyTree from './CampaignHierarchyTree.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';

const CampaignMappingList = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { showSnackBar } = useSnackBar();

  const {
    createCampaignMapping,
    updateCampaignMapping,
    fetchCampaignMappings,
    delteCampaignMapping
  } = LeadFormActions();

  const [childMappings, setChildMappings] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [parentCampaign, setParentCampaign] = useState<any>(null);
  const [childCampaign, setChildCampaign] = useState<any>(null);
  const [description, setDescription] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [hierarchyTree, setHierarchyTree] = useState(null);
  const [showTree, setShowTree] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const breadcrumbsData = [
    { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: "/" },
    { label: 'Campaigns', onClick: () => navigate('/r/records/v1') },
    { label: 'Mapping' },
  ];

  const getCampaignMapping = async () => {
    try {
      const res: any = await fetchCampaignMappings(campaignId);
      if (res?.status === 200) {
        setChildMappings(res.data.child_mappings || []);
        setHierarchyTree(res?.data?.hierarchy_tree || null);
      } else {
        showSnackBar('Failed to fetch mappings', 'error');
      }
    } catch (error) {
      showSnackBar('Error fetching mappings', 'error');
    }
  }

  useEffect(() => {
    // if (fetchedCampaigns.state === 'hasValue') {
    //   const raw = fetchedCampaigns.contents?.data || [];
    //   setCampaigns(raw);

    //   const selectedParent = raw.find(c => String(c.proposal_id) === String(campaignId));
    //   if (selectedParent) {
    //     setParentCampaign(selectedParent);

    //     fetchCampaignMappings(selectedParent.proposal_id)
    //       .then((res) => {
    //         if (res?.status === 200) {
    //           setChildMappings(res.data.child_mappings || []);
    //           setHierarchyTree(res.data.hierarchy_tree || null);
    //         } else {
    //           showSnackBar('Failed to fetch mappings', 'error');
    //         }
    //       })
    //       .catch((err) => {
    //         showSnackBar('Error fetching mappings', 'error');
    //       });
    //   }
    // }
    getCampaignMapping();
  }, [campaignId]);

  const handleOpenAddModal = () => {
    setChildCampaign(null);
    setDescription('');
    setEditIndex(null);
    setOpenModal(true);
  };

  const handleOpenEditModal = (index) => {
    const item: any = childMappings[index];
    const selectedChild = { proposal_id: item?.child_campaign_id, name: item?.child_campaign_name }//campaigns.find(c => c.proposal_id === item.child_campaign_id);
    setChildCampaign(selectedChild);
    setDescription(item.description || '');
    setEditIndex(index);
    setOpenModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!parentCampaign || !childCampaign) {
      showSnackBar('Please select both campaigns.', 'warning');
      return;
    }

    try {
      let response;
      if (editIndex !== null) {
        const item: { _id?: string } = childMappings[editIndex];
        let payload: any = {
          parent_campaign_id: parentCampaign?.proposal_id,
          parent_campaign_name: parentCampaign?.name,
          child_campaign_id: childCampaign?.proposal_id,
          child_campaign_name: childCampaign?.name,
          description: description || "Handles service requests",
          _id: item._id,
        };
        response = await updateCampaignMapping(payload);
      } else {
        let payload = {
          parent_campaign_id: parentCampaign?.proposal_id,
          parent_campaign_name: parentCampaign?.name,
          child_campaign_id: childCampaign?.proposal_id,
          child_campaign_name: childCampaign?.name,
          description: description || "Handles service requests",
        }
        response = await createCampaignMapping(payload);
      }

      if (response?.status === 200 || response?.status === 201) {
        showSnackBar(`Mapping ${editIndex !== null ? 'updated' : 'created'} successfully!`, 'success');
        const refresh = await fetchCampaignMappings(parentCampaign.proposal_id);
        if (refresh?.status === 200) {
          setChildMappings(refresh.data.child_mappings || []);
          setHierarchyTree(refresh.data.hierarchy_tree || null);
        }
        setOpenModal(false);
        setChildCampaign(null);
        setDescription('');
        setEditIndex(null);
      } else {
        showSnackBar('Failed to save campaign mapping', 'error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      showSnackBar('Error occurred while saving mapping', 'error');
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'parent_campaign_name',
      header: 'Parent Campaign',
      enableSorting: false,
      Cell: ({ row }) => row.original.parent_campaign_name,
        size: 400,
    },
    {
      accessorKey: 'child_campaign_name',
      header: 'Child Campaign',
      enableSorting: false,
      size: 330,
      Cell: ({ row }) => row.original.child_campaign_name,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: false,
      size: 250,
      Cell: ({ row }) => row.original.description || '-',
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 80,
      enableSorting: false,
      Cell: ({ row }) => {
        const [anchorElAction, setAnchorElAction] = React.useState<null | HTMLElement>(null);
        const actionOpen = Boolean(anchorElAction);
        const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorElAction(event.currentTarget);
        };
        const handleClose = () => {
          setAnchorElAction(null);
        };
        return (
          <div>
            <IconButton onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              id={`menu-${row.index}`}
              anchorEl={anchorElAction}
              open={actionOpen}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': `menu-button-${row.index}`,
              }}
            >
              <MenuItem onClick={() => { handleOpenEditModal(row.index); }}>
                Edit
              </MenuItem>
              <MenuItem onClick={() => { handleClickDelete(row.original) }}>
                Delete
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ], [childMappings]);

  const handleConfirmDelete = async () => {
    try {
      const res: any = await delteCampaignMapping(selectedRow?._id);
      if (res?.status === 200) {
        showSnackBar('Mapping deleted successfully', 'success');
        setOpenConfirmDialog(false);
        const updateList = childMappings?.filter((item: any) => item._id !== selectedRow?._id);
        setChildMappings(updateList);
      } else {
        showSnackBar("Failed to delete mapping", "error")
      }
    } catch (error) {
      showSnackBar("Failed to delete mapping", "error")
    }
  };
  const handleClickDelete = (rowData) => {
    setOpenConfirmDialog(true);
    setSelectedRow(rowData);
  }

  useEffect(() => {
    const localStorageWebFormData: any = JSON.parse(localStorage.getItem('leadWebFormData') || '{}');
    if (localStorageWebFormData?.campaign) {
      setParentCampaign(localStorageWebFormData?.campaign);
    }
  }, []);

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding:"5px" }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => item?.onClick?.()}
      />

      <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        <Grid item>
          <Typography variant="h4" fontWeight="bold">Campaign Mapping</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleOpenAddModal} sx={{ mr: 1 }} size='small'>
            Add Child Campaign
          </Button>
          {/* <Button variant="outlined" onClick={() => setShowTree(!showTree)} size='small'>
            {showTree ? 'Hide Hierarchy' : 'Show Hierarchy'}
          </Button> */}
        </Grid>
      </Grid>

      <Box mt={2}>
        <CommonTable
          columns={columns}
          data={childMappings}
        />
      </Box>

      <Collapse in={showTree}>
        {/* <Box mt={3} p={2} border="1px dashed #ccc" borderRadius="8px" overflow="auto">
          <Typography variant="h6" align="center" mb={2}>
            Tree View of {hierarchyTree?.campaign_name}
          </Typography>

          {hierarchyTree && renderGraph(hierarchyTree)}
        </Box> */}
        <CampaignHierarchyTree treeData={hierarchyTree} />
      </Collapse>
      {openModal &&
        <CreatEditMappingModal
          handleConfirmSubmit={handleConfirmSubmit}
          open={openModal} onClose={() => setOpenModal(false)}
          parentCampaign={parentCampaign}
          setChildCampaign={(value) => setChildCampaign(value)}
          childCampaign={childCampaign}
          description={description}
          setDescription={(value) => setDescription(value)}
          editIndex={editIndex}
        />
      }
      <ConfirmBox
        open={openConfirmDialog}
        onClose={(confirmed) => {
          if (confirmed) {
            handleConfirmDelete();
          } else {
            setOpenConfirmDialog(false);
          }
        }}
        options={{
          title: 'Delete mapping',
          text: `Are you sure you want to delete maapping`,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />

    </Box>
  );
};

export default CampaignMappingList;
